import { PointCloudAnalysis } from "@/store/point-cloud-analysis-tool-slice";
import { getAnalysisReferencePlane } from "@/utils/colormap-analysis-utils";
import { GridPlane } from "@faro-lotv/app-component-toolbox";
import { useEffect, useMemo, useState, type JSX } from "react";
import { Box3, Quaternion, Vector2, Vector3 } from "three";

type ReferencePlaneRendererProps = {
  analysis: PointCloudAnalysis;
};

/** @returns The renderer of reference plane for an analysis */
export function ReferencePlaneRenderer({
  analysis,
}: ReferencePlaneRendererProps): JSX.Element | null {
  const polygon = useMemo(
    () =>
      (analysis.polygonSelection ?? []).map((p) => new Vector3().fromArray(p)),
    [analysis.polygonSelection],
  );

  const referencePlane = useMemo(
    () => getAnalysisReferencePlane(analysis),
    [analysis],
  );

  const [planePosition, setPlanePosition] = useState<Vector3>();
  const [planeQuaternion, setPlaneQuaternion] = useState<Quaternion>();
  const extents = useMemo(() => {
    const box = new Box3().setFromPoints(polygon);
    const size = box.getSize(new Vector3()).length();
    return new Vector2(size, size);
  }, [polygon]);

  useEffect(() => {
    if (referencePlane) {
      setPlanePosition(referencePlane.point);
      const quaternion = new Quaternion().setFromUnitVectors(
        new Vector3(0, 0, 1),
        referencePlane.normal,
      );
      setPlaneQuaternion(quaternion);
    }
  }, [referencePlane]);

  if (!referencePlane) {
    return null;
  }

  return (
    <group position={planePosition} quaternion={planeQuaternion}>
      <GridPlane size={extents} />
    </group>
  );
}
